/**
 * Voltage formatting utilities (electrical potential)
 */

import { isNil } from "./number";
import { FormatRegistry } from "./registry";
import type { FieldValue, FormattedValue, VoltageFormat } from "./types";

/**
 * Format as millivolts
 */
export const toMillivolts = (value: number | null | undefined, decimals: number = 0): string | null => {
  if (isNil(value)) return null;
  const mV = value * 1000;
  return decimals === 0 ? `${Math.round(mV)} mV` : `${mV.toFixed(decimals)} mV`;
};

/**
 * Format as volts
 */
export const toVolts = (value: number | null | undefined, decimals: number = 0): string | null => {
  if (isNil(value)) return null;
  return decimals === 0 ? `${Math.round(value)} V` : `${value.toFixed(decimals)} V`;
};

/**
 * Format as kilovolts
 */
export const toKilovolts = (value: number | null | undefined, decimals: number = 1): string | null => {
  if (isNil(value)) return null;
  return `${(value / 1000).toFixed(decimals)} kV`;
};

/**
 * Auto-scale voltage to the most appropriate unit
 */
export const autoScaleVoltage = (value: number | null | undefined, decimals?: number): string | null => {
  if (isNil(value)) return null;

  const absValue = Math.abs(value);

  if (absValue >= 1000) {
    return toKilovolts(value, decimals ?? 1);
  } else if (absValue < 1) {
    return toMillivolts(value, decimals ?? 0);
  } else {
    return toVolts(value, decimals ?? 0);
  }
};

/**
 * Format voltage according to VoltageFormat specification
 */
export const formatVoltage = (value: FieldValue, format: VoltageFormat): FormattedValue => {
  if (format.hideWhenNull && (value === null || value === undefined)) return null;
  if (format.hideWhenZero && value === 0) return null;
  if (value == null) return format.nullText ?? null;

  const numValue = typeof value === "string" ? parseFloat(value) : Number(value);
  if (isNaN(numValue)) return format.nullText ?? null;

  const decimals = format.decimals;

  // If a specific unit is provided, use it
  if (format.unit) {
    switch (format.unit) {
      case "kV":
        return toKilovolts(numValue, decimals ?? 1);
      case "mV":
        return toMillivolts(numValue, decimals ?? 0);
      case "V":
        return toVolts(numValue, decimals ?? 0);
    }
  }

  // Default to auto-scaling unless explicitly disabled
  if (format.autoScale !== false) {
    return autoScaleVoltage(numValue, decimals);
  }

  // If auto-scale is explicitly false and no unit specified, use base volts
  return toVolts(numValue, decimals ?? 0);
};

// Register the formatter
// biome-ignore lint/suspicious/noExplicitAny: Required for FormatRegistry type compatibility
FormatRegistry.register("voltage", formatVoltage as any);

// Convenience exports with shorter names
export const toMV = toMillivolts;
export const toV = toVolts;
export const toKV = toKilovolts;
